import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { UserStatus } from '@/entities/vfo/types';
import {
  ApiChatResponse,
  ApiMessageResponse,
  ApiParticipantResponse,
} from '@/shared/api/generated/data-contracts';
import { arrays } from '@/shared/utils/arrays';

type ChatState = {
  chats: ApiChatResponse[];
  isLoading: boolean;
  activeChatId: string | null;
  unreadCounts: Record<string, number>;
  participantStatus: Record<number, UserStatus>;
  isInitialized: boolean;
  hasNotificationSounds: boolean;
};

type ChatStore = ChatState & {
  setChats: (chats: ApiChatResponse[]) => void;
  setLoading: (isLoading: boolean) => void;
  setActiveChatId: (chatId: string | null) => void;
  setUnreadCounts: (unreadCounts: Record<string, number>) => void;
  markChatAsRead: (chatId: string) => void;
  incrementUnreadCount: (chatId: string) => void;
  setParticipantStatus: (status: Record<number, UserStatus>) => void;
  addParticipantToChat: (chatId: string, participant: ApiParticipantResponse) => void;
  removeParticipantFromChat: (chatId: string, profileId: number) => void;

  addMessage: (chatId: string, message: ApiMessageResponse) => void;
  setMessages: (chatId: string, messages: ApiMessageResponse[]) => void;
  prependMessages: (chatId: string, messages: ApiMessageResponse[]) => void;

  removeChat: (chatId: string) => void;
  moveChatToTop: (chatId: string) => void;
  moveChatAfterUnread: (chatId: string) => void;

  setIsInitialized: (isInitialized: boolean) => void;
  setHasNotificationSounds: (hasNotificationSounds: boolean) => void;
};

export const useChatStore = create<ChatStore>()(
  persist(
    (set) => ({
      chats: [],
      isLoading: false,
      activeChatId: null,
      unreadCounts: {},
      participantStatus: {},
      isInitialized: false,
      hasNotificationSounds: true,

      setChats: (chats) => set({ chats }),
      setLoading: (isLoading) => set({ isLoading }),
      setActiveChatId: (activeChatId) => set({ activeChatId }),
      setUnreadCounts: (unreadCounts) => set({ unreadCounts }),
      markChatAsRead: (chatId) =>
        set((state) => ({
          unreadCounts: {
            ...state.unreadCounts,
            [chatId]: 0,
          },
        })),
      incrementUnreadCount: (chatId) =>
        set((state) => {
          return {
            unreadCounts: {
              ...state.unreadCounts,
              [chatId]: (state.unreadCounts[chatId] || 0) + 1,
            },
          };
        }),
      setParticipantStatus: (participantStatus) => set({ participantStatus }),

      addMessage: (chatId, message) =>
        set((state) => ({
          chats: state.chats.map((chat) =>
            chat.id === chatId
              ? {
                  ...chat,
                  messages: [...chat.messages, message],
                  updatedAt: message.createdAt,
                }
              : chat,
          ),
        })),

      setMessages: (chatId, messages) =>
        set((state) => ({
          chats: state.chats.map((chat) => (chat.id === chatId ? { ...chat, messages } : chat)),
        })),
      prependMessages: (chatId, messages) =>
        set((state) => ({
          chats: state.chats.map((chat) =>
            chat.id === chatId ? { ...chat, messages: [...messages, ...chat.messages] } : chat,
          ),
        })),

      removeChat: (chatId) =>
        set((state) => ({
          chats: state.chats.filter((chat) => chat.id !== chatId),
          activeChatId: state.activeChatId === chatId ? null : state.activeChatId,
        })),

      moveChatToTop: (chatId) =>
        set((state) => ({
          chats: arrays.moveToFirst(state.chats, (chat) => chat.id === chatId),
        })),

      moveChatAfterUnread: (chatId) =>
        set((state) => ({
          chats: arrays.moveTargetAfterCondition(
            state.chats,
            (chat) => chat.id === chatId,
            (chat) => (state.unreadCounts[chat.id] ?? 0) > 0,
          ),
        })),

      addParticipantToChat: (chatId, participant) =>
        set((state) => ({
          chats: state.chats.map((chat) =>
            chat.id === chatId
              ? { ...chat, participants: [...chat.participants, participant] }
              : chat,
          ),
        })),

      removeParticipantFromChat: (chatId, profileId) =>
        set((state) => ({
          chats: state.chats.map((chat) =>
            chat.id === chatId
              ? {
                  ...chat,
                  participants: chat.participants.filter((p) => p.profileId !== profileId),
                }
              : chat,
          ),
        })),

      setIsInitialized: (isInitialized) => set({ isInitialized }),

      setHasNotificationSounds: (hasNotificationSounds) => set({ hasNotificationSounds }),
    }),
    {
      name: 'vfo-chat-storage',
      partialize: (state) => ({
        activeChatId: state.activeChatId,
        hasNotificationSounds: state.hasNotificationSounds,
      }),
    },
  ),
);
